import moment = require("moment");
import JSONAlarmsCount = DashboardRequestsData.JSONAlarmsCount;
import * as React from "react";
import {CardBoxSmall} from "../../../components/CardBox";
import {Title} from "../../../components/Title";
import {BarChart, BarChartGroup} from "../../../locations/marketing/charts/BarChartWithGroups";
import {Loader} from "../../../components/Utils/loader/Loader";
import {StatisticsUtils} from "../../../components/Utils/statistics/StatisticsUtils";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {AlarmsManagementTexts, WidgetResponse} from "../../DashboardContainer";
import {DashboardRequestsData} from "../../data/DashboardData";
import {JSONMapper} from "../../data/JSONMapper";
import {FormatLocaleNumber} from "../DashboardUtils";
import {RestDataLoader} from "./RestDataLoader";
import "./style/AlarmsManagementWidget.scss";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {WidgetBadge, WidgetBadgeType} from "../DashboardWidget";
import AutoSizer from "react-virtualized-auto-sizer";

declare var AppTranslations: AppTranslations;

interface AlarmsManagementWidgetProps {
    data: WidgetResponse;
    texts: AlarmsManagementTexts;
}

interface WidgetState {
    ready: boolean;
    error: boolean;
    openedAlarmsLessThen2Weeks: number;
    openedAlarmsMoreThen2Weeks: number;
    closedAlarmsLessThen2Weeks: number;
    closedAlarmsByDate: DashboardRequestsData.JSONAlarmsByDateCount[];
    openAlarmsByDate: DashboardRequestsData.JSONAlarmsByDateCount[];
    averageAgeOpenedAlarms: number;
    averageAgeClosedAlarmsLessThen2Weeks: number;
}

export interface ClosedOpenedAlarmByDay {
    dayNumber: string;
    openedAlarms: number;
    closedAlarms: number;
}

export class AlarmsManagementWidget extends React.Component<AlarmsManagementWidgetProps, WidgetState> {
    constructor(props: AlarmsManagementWidgetProps) {
        super(props);
        this.state = {
            ready: false,
            error: false,
            openedAlarmsLessThen2Weeks: null,
            openedAlarmsMoreThen2Weeks: null,
            closedAlarmsLessThen2Weeks: null,
            closedAlarmsByDate: null,
            openAlarmsByDate: null,
            averageAgeOpenedAlarms: null,
            averageAgeClosedAlarmsLessThen2Weeks: null,
        };
    }

    public ready() {
        if (this.state.openedAlarmsLessThen2Weeks != null
            && this.state.openedAlarmsMoreThen2Weeks != null
            && this.state.openAlarmsByDate != null
            && this.state.closedAlarmsByDate != null
            && this.state.closedAlarmsLessThen2Weeks != null
            && this.state.averageAgeClosedAlarmsLessThen2Weeks != null
            && this.state.averageAgeOpenedAlarms != null
        ) {
            this.setState({ready: true});
        }
    }

    public componentDidMount() {
        const date = moment().utc().startOf("day").add(-14, "day");
        RestDataLoader.loadData(
            "statistics/alarms/counting",
            {
                statuses: "pending,managed",
                openedAfterDate: date.format(),
                placements: "warehouse,market",
            },
            (data: JSONAlarmsCount) => {
                this.setState({
                    openedAlarmsLessThen2Weeks: data.count,
                });
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
        RestDataLoader.loadData(
            "statistics/alarms/counting",
            {
                statuses: "pending,managed",
                openedBeforeDate: date.format(),
                placements: "warehouse,market",
            },
            (data: JSONAlarmsCount) => {
                this.setState({openedAlarmsMoreThen2Weeks: data.count});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
        const upperLimit = moment().utc().startOf("day");
        RestDataLoader.loadData(
            "statistics/alarms/counting/firstSignalDate",
            {
                beginDate: date.format().toString(),
                endDate: upperLimit.format().toString(),
                placements: "warehouse,market",
            },
            (data: any) => {
                this.setState({openAlarmsByDate: data.list});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );
        RestDataLoader.loadData(
            "statistics/alarms/counting/closureDate",
            {
                beginDate: date.format().toString(),
                endDate: upperLimit.format().toString(),
                placements: "warehouse,market",
            },
            (data: any) => {
                this.setState({closedAlarmsByDate: data.list});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );

        RestDataLoader.loadData(
            "statistics/alarms/counting",
            {
                statuses: "recentlyClosed,closed",
                closedAfterDate: date.format(),
                placements: "warehouse,market",
            },
            (data: any) => {
                this.setState({
                    closedAlarmsLessThen2Weeks: data.count,
                    averageAgeClosedAlarmsLessThen2Weeks: data.ageAvg,
                });
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );

        RestDataLoader.loadData(
            "statistics/alarms/counting",
            {
                statuses: "pending,managed",
                placements: "warehouse,market",
            },
            (data: any) => {
                this.setState({averageAgeOpenedAlarms: data.ageAvg});
                this.ready();
            },
            () => {
                this.setState({error: true});
            },
        );

    }

    public render() {
        if (this.state.error) {
            return null;
        }
        if (!this.state.ready) {
            return (
                <div className="alarms-management-widget">
                    <Loader/>
                </div>
            );
        }
        const data: BarChartGroup[] = JSONMapper.jsonAlarmsByDateMapper(
            this.state.openAlarmsByDate,
            this.state.closedAlarmsByDate,
        ).map(
            (item, i) => {
                return {
                    name: "" + item.dayNumber,
                    elements: [
                        {
                            name: "#" + this.props.texts.openedAlarms,
                            color: "rgb(245, 5, 10)",
                            value: item.openedAlarms,
                        },
                        {
                            name: "#" + this.props.texts.closedAlarms,
                            color: "rgb(128,128,128)",
                            value: item.closedAlarms,
                        },
                    ],
                };
            },
        );
        const content = (
            <VeBoxCard className="alarms-management-widget__container home-dashboard-card">
                <VeBoxCard.Header
                    className="alarms-management-widget__header technical-header space-between"
                    title={this.props.texts.title}
                    titleClassName={"lowercase capitalize"}
                    singleLineTitle={true}
                    back={
                        <WidgetBadge type={WidgetBadgeType.TECHNICAL}/>
                    }
                />
                <VeBoxCard.Body className="alarms-management-widget__content">
                    <div className="cards-container">
                        <div className="top-card-container">
                            <CardBoxSmall
                                title={this.props.texts.openedAlarmsMore2Weeks}
                                value={FormatLocaleNumber(this.state.openedAlarmsMoreThen2Weeks)}
                                bolder={true}
                            />
                            <CardBoxSmall
                                title={this.props.texts.openedAlarmsLess2Weeks}
                                value={FormatLocaleNumber(this.state.openedAlarmsLessThen2Weeks)}
                                bolder={true}
                            />
                            <CardBoxSmall
                                title={this.props.texts.closedAlarmsLess2Weeks}
                                value={FormatLocaleNumber(this.state.closedAlarmsLessThen2Weeks)}
                                bolder={true}
                            />
                            <CardBoxSmall
                                title={this.props.texts.openedAlarmsAverageAge}
                                value={StatisticsUtils.periodInWords(
                                    this.state.averageAgeOpenedAlarms,
                                    this.props.texts.timeTexts,
                                    2)
                                }
                                bolder={true}
                            />
                            <CardBoxSmall
                                title={this.props.texts.closedAlarmsAverageAge}
                                value={StatisticsUtils.periodInWords(
                                    this.state.averageAgeClosedAlarmsLessThen2Weeks,
                                    this.props.texts.timeTexts,
                                    2)
                                }
                                bolder={true}
                            />
                        </div>
                    </div>
                    <Title title={this.props.texts.openedClosedAlarmsLast2Weeks}/>
                    <div className="chart-container">
                        <AutoSizer style={{width: "auto"}}>
                            {({width, height}) =>
                                <BarChart
                                    width={width}
                                    height={height}
                                    color={null}
                                    data={data}
                                    yUnitOfMeasure={this.props.texts.alarmsUnitOfMeasure}
                                />}
                        </AutoSizer>
                    </div>
                </VeBoxCard.Body>
            </VeBoxCard>
        );

        return (
            <div className="alarms-management-widget">
                {content}
            </div>
        );
    }
}
